// src/Admin/Projectsmanagement.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiPath, absUrl } from "../api/http";
import {
  FaPlus,
  FaSync,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaTrash,
  FaEdit,
  FaEye,
  FaImages,
  FaCheck,
  FaTimes,
  FaFilter,
  FaSortAmountDown,
  FaCloudUploadAlt,
  FaBars,
  FaUsers,
  FaStar,
  FaInfoCircle,
} from "react-icons/fa";

/* =========================
  Constants
  ========================= */
const STATUS = {
  ONGOING: "ongoing",
  COMPLETED: "completed",
};

const PLACEHOLDER = "/images/placeholder.jpg";
const THUMB_PLACEHOLDER = "/images/placeholder-thumb.jpg";

/* =========================
  Utilities
  ========================= */
function makeImageCandidates(src) {
  if (!src || (typeof src === "string" && !src.trim())) return [];
  if (/^(blob:|data:)/i.test(src)) return [src];

  const base = absUrl(src);
  const set = new Set();
  const add = (s) => s && !set.has(s) && set.add(s);

  add(base);
  if (base.includes("/uploads/")) add(base.replace("/uploads/", "/storage/"));
  if (base.includes("/storage/")) add(base.replace("/storage/", "/uploads/"));
  if (base.includes("/public/")) add(base.replace("/public/", "/storage/"));
  if (base.includes("/storage/app/public/")) add(base.replace("/storage/app/public/", "/storage/"));
  return Array.from(set);
}

function toServerPathVariants(urlOrPath) {
  if (!urlOrPath) return [];
  if (/^(blob:|data:)/i.test(urlOrPath)) return [];
  let pathname = "";
  try {
    const u = new URL(urlOrPath, window.location.origin);
    pathname = u.pathname || urlOrPath;
  } catch {
    pathname = urlOrPath;
  }
  if (!pathname.startsWith("/")) pathname = "/" + pathname;

  const set = new Set();
  const add = (s) => s && set.add(s);

  add(pathname);
  if (pathname.includes("/storage/")) add(pathname.replace("/storage/", "/uploads/"));
  if (pathname.includes("/uploads/")) add(pathname.replace("/uploads/", "/storage/"));
  if (pathname.includes("/public/")) add(pathname.replace("/public/", "/storage/"));
  if (pathname.includes("/storage/app/public/"))
    add(pathname.replace("/storage/app/public/", "/storage/"));

  const noSlash = pathname.replace(/^\//, "");
  add(noSlash);
  if (noSlash.includes("storage/")) add(noSlash.replace("storage/", "uploads/"));
  if (noSlash.includes("uploads/")) add(noSlash.replace("uploads/", "storage/"));
  if (noSlash.includes("public/")) add(noSlash.replace("public/", "storage/"));
  if (noSlash.includes("storage/app/public/"))
    add(noSlash.replace("storage/app/public/", "storage/"));

  return Array.from(set);
}

async function urlToFile(url, fallbackName = "cover.jpg") {
  try {
    const res = await fetch(absUrl(url), { mode: "cors", credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const ext = (blob.type && blob.type.split("/")[1]) || "jpg";
    const name = fallbackName.includes(".") ? fallbackName : `${fallbackName}.${ext}`;
    return new File([blob], name, { type: blob.type || "image/jpeg" });
  } catch {
    return null;
  }
}

function SafeImg({ src, alt, className, fallback = PLACEHOLDER }) {
  const candidates = useMemo(() => makeImageCandidates(src), [src]);
  const [idx, setIdx] = useState(0);
  const [finalSrc, setFinalSrc] = useState(candidates[0] || fallback);

  useEffect(() => {
    setIdx(0);
    setFinalSrc(candidates[0] || fallback);
  }, [candidates, fallback]);

  const onError = useCallback(() => {
    if (idx + 1 < candidates.length) {
      setIdx((i) => i + 1);
      setFinalSrc(candidates[idx + 1]);
    } else if (finalSrc !== fallback) {
      setFinalSrc(fallback);
    }
  }, [idx, candidates, finalSrc, fallback]);

  return (
    <img
      src={finalSrc}
      alt={alt}
      className={className}
      onError={onError}
      loading="lazy"
      decoding="async"
    />
  );
}

const Badge = ({ kind = "neutral", children }) => {
  const map = {
    neutral: "bg-gray-100 text-gray-700",
    info: "bg-cyan-100 text-cyan-700",
    success: "bg-green-100 text-green-700",
    warning: "bg-amber-100 text-amber-700",
    danger: "bg-red-100 text-red-700",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${map[kind]}`}>
      {children}
    </span>
  );
};

const StatCard = ({ label, value, icon, ok }) => (
  <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-sm text-gray-500">{label}</div>
        <div className="mt-1 text-2xl font-extrabold text-gray-800">{value}</div>
      </div>
      <div
        className={`w-10 h-10 grid place-items-center rounded-lg ${
          ok ? "bg-green-100 text-green-600" : "bg-cyan-100 text-cyan-600"
        }`}
      >
        {icon}
      </div>
    </div>
  </div>
);

const FieldError = ({ errors, name }) => {
  const msgs = errors?.[name];
  if (!msgs || !msgs.length) return null;
  return <p className="mt-1 text-xs text-red-600">{msgs[0]}</p>;
};

function formatMillionsLKR(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  const m = n / 1_000_000;
  return `LKR ${m.toLocaleString(undefined, { maximumFractionDigits: 2 })} M`;
}

function errorsToString(errs) {
  if (!errs || typeof errs !== "object") return "";
  try {
    const parts = [];
    for (const k of Object.keys(errs)) {
      const v = errs[k];
      if (Array.isArray(v)) parts.push(`${k}: ${v.join(", ")}`);
      else if (typeof v === "string") parts.push(`${k}: ${v}`);
      else parts.push(`${k}: ${JSON.stringify(v)}`);
    }
    return parts.join(" | ");
  } catch {
    return String(errs);
  }
}

/* =========================
  Toasts (popup cards)
  ========================= */
function Toast({ t, onClose }) {
  const base =
    t.type === "success"
      ? "border-green-300 bg-green-50 text-green-800"
      : t.type === "error"
      ? "border-red-300 bg-red-50 text-red-800"
      : "border-cyan-300 bg-cyan-50 text-cyan-800";
  return (
    <div className={`w-80 max-w-[90vw] rounded-xl border p-3 shadow ${base}`}>
      <div className="flex items-start gap-2">
        <FaInfoCircle className="mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold text-sm">{t.title}</div>
          {t.message && <div className="text-xs mt-0.5">{t.message}</div>}
        </div>
        <button onClick={() => onClose(t.id)} className="text-inherit/70 hover:text-inherit">✕</button>
      </div>
    </div>
  );
}

function useToasts() {
  const [toasts, setToasts] = useState([]);
  const push = useCallback((type, title, message = "") => {
    const id = Math.random().toString(36).slice(2);
    const t = { id, type, title, message };
    setToasts((arr) => [t, ...arr]);
    setTimeout(() => {
      setToasts((arr) => arr.filter((x) => x.id !== id));
    }, 3800);
  }, []);
  const remove = useCallback((id) => setToasts((arr) => arr.filter((x) => x.id !== id)), []);
  const View = useCallback(
    () => (
      <div className="fixed right-4 top-4 z-[100] space-y-2">
        {toasts.map((t) => (
          <Toast key={t.id} t={t} onClose={remove} />
        ))}
      </div>
    ),
    [toasts, remove]
  );
  return { push, View };
}

/* =========================
  Confirm dialog
  ========================= */
function Confirm({ open, title, message, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90]">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200">
          <div className="p-4">
            <div className="text-lg font-extrabold text-gray-800">{title || "Are you sure?"}</div>
            {message && <div className="text-sm text-gray-600 mt-1">{message}</div>}
          </div>
          <div className="px-4 py-3 flex justify-end gap-2 border-t">
            <button className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50" onClick={onCancel}>
              Cancel
            </button>
            <button className="px-3 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600" onClick={onConfirm}>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
  Gallery (preview modal)
  ========================= */
function Gallery({ images = [], currentIndex, setCurrentIndex }) {
  const imgs = Array.isArray(images)
    ? images.map((s) => (typeof s === "string" ? s.trim() : "")).filter(Boolean)
    : [];
  const hasImages = imgs.length > 0;
  const safeIndex = hasImages ? Math.max(0, Math.min(currentIndex, imgs.length - 1)) : 0;

  const go = (dir) => {
    if (!hasImages) return;
    setCurrentIndex((i) => (i + dir + imgs.length) % imgs.length);
  };

  if (!hasImages) {
    return (
      <div className="rounded-2xl bg-gray-100 p-6 grid place-items-center text-gray-400">
        No images to display
      </div>
    );
  }

  return (
    <div>
      <div className="relative bg-gray-100 rounded-2xl p-4">
        <button
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-8 h-8 md:w-9 md:h-9 rounded-full bg-white border border-gray-200 grid place-items-center hover:shadow"
          onClick={() => go(-1)}
          aria-label="Previous"
        >
          <FaChevronLeft className="text-sm" />
        </button>
        <button
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-8 h-8 md:w-9 md:h-9 rounded-full bg-white border border-gray-200 grid place-items-center hover:shadow"
          onClick={() => go(1)}
          aria-label="Next"
        >
          <FaChevronRight className="text-sm" />
        </button>

        <SafeImg
          src={imgs[safeIndex]}
          alt={`slide-${safeIndex + 1}`}
          className="w-full h-48 md:h-64 lg:h-80 object-cover rounded-xl"
          fallback={PLACEHOLDER}
        />
      </div>

      <div className="mt-4 flex gap-2 md:gap-3 overflow-x-auto pb-1">
        {imgs.map((src, idx) => (
          <button
            key={`${idx}-${src}`}
            onClick={() => setCurrentIndex(idx)}
            className={`rounded-xl border-2 overflow-hidden transition ${
              idx === safeIndex ? "border-cyan-400" : "border-transparent hover:border-gray-300"
            }`}
          >
            <SafeImg
              src={src}
              alt={`thumb-${idx + 1}`}
              className="w-16 h-12 md:w-20 md:h-14 object-cover"
              fallback={THUMB_PLACEHOLDER}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

/* =========================
  Modal shell
  ========================= */
function Modal({ open, onClose, children, size = "lg" }) {
  if (!open) return null;
  const maxW =
    size === "sm"
      ? "max-w-lg"
      : size === "lg"
      ? "max-w-4xl"
      : size === "xl"
      ? "max-w-5xl"
      : "max-w-3xl";
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
        <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity" onClick={onClose}></div>
        <div className={`inline-block w-full ${maxW} p-2 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl`}>
          {children}
        </div>
      </div>
    </div>
  );
}

/* =========================
  Create / Edit Project Form
  ========================= */
const emptyForm = {
  title: "",
  blurb: "",
  owner: "",
  address: "",
  priceMillions: "",
  status: STATUS.ONGOING,
  images: [],
  replaceGallery: false,
};

function ProjectForm({ open, onClose, onSaved, initial }) {
  const [values, setValues] = useState(emptyForm);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const { push: toast, View: Toasts } = useToasts();
  const [confirm, setConfirm] = useState({ open: false });

  const [existingList, setExistingList] = useState([]);
  const [markedDeletes, setMarkedDeletes] = useState(new Set());
  const [coverExisting, setCoverExisting] = useState(null);
  const [coverNewIdx, setCoverNewIdx] = useState(-1);

  useEffect(() => {
    if (initial) {
      const millions = Number(initial.price) ? Number(initial.price) / 1_000_000 : "";
      setValues({
        title: initial.title || "",
        blurb: initial.blurb || "",
        owner: initial.owner || "",
        address: initial.address || "",
        priceMillions: millions === "" ? "" : String(millions),
        status: initial.status || STATUS.ONGOING,
        images: [],
        replaceGallery: false,
      });
      const list = [initial.img, ...(Array.isArray(initial.images) ? initial.images : [])]
        .map((s) => (typeof s === "string" ? s.trim() : ""))
        .filter(Boolean);
      setExistingList(list);
      setCoverExisting(list[0] || null);
    } else {
      setValues(emptyForm);
      setExistingList([]);
      setCoverExisting(null);
    }
    setMarkedDeletes(new Set());
    setPreview([]);
    setErrors({});
    setCoverNewIdx(-1);
  }, [initial, open]);

  useEffect(() => {
    return () => {
      try {
        (preview || []).forEach((u) => {
          if (typeof u === "string" && u.startsWith("blob:")) URL.revokeObjectURL(u);
        });
      } catch {}
    };
  }, [preview]);

  const onFiles = (files) => {
    const arr = Array.from(files || []);
    const urls = arr.map((f) => URL.createObjectURL(f));
    setValues((v) => ({ ...v, images: arr }));
    setPreview(urls);
    toast("info", "Images selected", "Click a new thumbnail to set cover, or trash to remove.");
  };

  const chooseCoverFromExisting = (src) => {
    if (markedDeletes.has(src)) {
      toast("error", "Cannot set as cover", "This image is marked for deletion. Undo delete first.");
      return;
    }
    setCoverExisting(src);
    setCoverNewIdx(-1);
  };
  const chooseCoverFromNew = (idx) => {
    setCoverNewIdx(idx);
    setCoverExisting(null);
  };

  const coverPreviewSrc = useMemo(() => {
    if (coverNewIdx >= 0 && preview[coverNewIdx]) return preview[coverNewIdx];
    if (coverExisting) return coverExisting;
    if (preview[0]) return preview[0];
    return existingList.find((x) => !markedDeletes.has(x)) || null;
  }, [coverNewIdx, preview, coverExisting, existingList, markedDeletes]);

  const askDeleteNew = (idx) => {
    const run = () => {
      setConfirm({ open: false });
      setValues((v) => {
        const files = v.images.filter((_, i) => i !== idx);
        return { ...v, images: files };
      });
      setPreview((p) => p.filter((_, i) => i !== idx));
      if (coverNewIdx === idx) setCoverNewIdx(-1);
      else if (coverNewIdx > idx) setCoverNewIdx((i) => i - 1);
      toast("success", "Removed image", "The selected upload was removed.");
    };
    setConfirm({
      open: true,
      title: "Remove uploaded image?",
      message: "This only removes it from this edit (not saved to server).",
      onConfirm: run,
      onCancel: () => setConfirm({ open: false }),
    });
  };

  const toggleMarkDeleteExisting = (src) => {
    const run = () => {
      setConfirm({ open: false });
      setMarkedDeletes((prev) => {
        const n = new Set(prev);
        if (n.has(src)) n.delete(src);
        else n.add(src);
        if (n.has(coverExisting)) {
          const next = existingList.find((x) => !n.has(x)) || null;
          setCoverExisting(next);
        }
        return n;
      });
      toast(
        "success",
        markedDeletes.has(src) ? "Delete undone" : "Image marked for delete",
        markedDeletes.has(src) ? "This image will be kept." : "It will be removed when you Save."
      );
    };
    if (!markedDeletes.has(src)) {
      setConfirm({
        open: true,
        title: "Delete image on save?",
        message: "This will be removed from the project when you click Save.",
        onConfirm: run,
        onCancel: () => setConfirm({ open: false }),
      });
    } else {
      run();
    }
  };

  const handleSave = async () => {
    setErrors({});

    const local = {};
    if (!values.title || !values.title.trim()) local.title = ["Title is required"];
    if (values.priceMillions !== "" && values.priceMillions != null) {
      const num = Number(String(values.priceMillions).replace(/,/g, ""));
      if (Number.isNaN(num)) local.price = ["Contract value (millions) must be a number"];
    }
    if (Object.keys(local).length) {
      setErrors(local);
      toast("error", "Fix the highlighted fields", "Please fill required fields correctly.");
      return;
    }

    const fd = new FormData();
    fd.append("title", values.title.trim());
    fd.append("status", values.status || STATUS.ONGOING);

    if (values.blurb) {
      fd.append("blurb", values.blurb);
      fd.append("description", values.blurb);
    }
    if (values.owner) {
      fd.append("owner", values.owner);
      fd.append("client", values.owner);
    }
    if (values.address) {
      fd.append("address", values.address);
      fd.append("location", values.address);
    }
    if (values.priceMillions !== "" && values.priceMillions != null) {
      const numM = Number(String(values.priceMillions).replace(/,/g, ""));
      const full = Math.round(numM * 1_000_000);
      fd.append("price", String(full));
      fd.append("contract_value", String(full));
    }

    // ======= NEW: decide cover file and gallery uploads (avoid duplicating cover in images[]) =======
    let coverFile = null;
    let galleryUploads = [...values.images];

    if (coverNewIdx >= 0 && values.images[coverNewIdx]) {
      // Cover chosen from NEW uploads: exclude it from gallery
      coverFile = values.images[coverNewIdx];
      galleryUploads = values.images.filter((_, i) => i !== coverNewIdx);
    } else if (!coverExisting && values.images.length > 0) {
      // No explicit cover selected; pick first new upload as cover by default
      coverFile = values.images[0];
      galleryUploads = values.images.slice(1);
    }
    // ================================================================================================

    // Append gallery files (EXCLUDING coverFile if any)
    galleryUploads.forEach((f) => fd.append("images[]", f));
    if (values.replaceGallery) fd.append("replace", "1");

    // Removals unchanged
    const removeList = Array.from(markedDeletes);
    if (removeList.length) {
      removeList.forEach((p) => {
        toServerPathVariants(p).forEach((v) => fd.append("remove_images[]", v));
      });
    }

    // Send the cover exactly once:
    if (coverFile) {
      // Use ONE canonical key (adjust to your backend; here we use 'img')
      fd.append("img", coverFile);
      // NOTE: DO NOT also append this same file to images[] – already excluded above.
    } else if (coverExisting) {
      // If the cover comes from existing and it's different from current initial.img, set by path
      const changed = initial?.img && coverExisting && initial.img !== coverExisting;
      if (changed) {
        // Prefer path-based set to avoid re-upload
        const variants = toServerPathVariants(coverExisting);
        fd.append("set_cover_from_existing", "1");
        if (variants[0]) {
          fd.append("cover_existing", variants[0]);
          // fd.append("cover_path", variants[0]); // optional alias if backend recognizes it
        }
        variants.forEach((v) => fd.append("cover_existing_variants[]", v));
      }
    }

    const isEdit = Boolean(initial?.id);
    const url = isEdit ? apiPath(`/projects/${initial.id}`) : apiPath("/projects");
    if (isEdit) fd.append("_method", "PUT");

    try {
      setSaving(true);
      await api.post(url, fd);
      toast("success", "Project saved", "Your changes have been saved.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data;
      if (status === 422) {
        const errs = body?.errors || {};
        setErrors(errs);
        console.error("Validation 422:", errs);
        toast("error", "Validation failed", errorsToString(errs) || body?.message || "Unprocessable content.");
      } else if (status === 405 || status === 404) {
        try {
          const fd2 = new FormData();
          for (const [k, v] of fd) {
            if (k !== "_method") fd2.append(k, v);
          }
          fd2.append("_method", "PATCH");
          await api.post(url, fd2);
          toast("success", "Project saved (fallback)", "Your changes have been saved.");
          onSaved?.();
          onClose?.();
          return;
        } catch (e2) {
          const b2 = e2?.response?.data;
          console.error("Save failed (fallback):", b2 || e2.message);
          toast("error", "Save failed", b2?.message || e2.message || "Network or server error.");
        }
      } else {
        console.error("Save failed:", body || e.message);
        toast("error", "Save failed", body?.message || e.message || "Network or server error.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="xl">
      <Toasts />
      <Confirm
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        onCancel={confirm.onCancel}
        onConfirm={confirm.onConfirm}
      />

      <div className="p-4 sm:p-6">
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-extrabold text-gray-800">
            {initial?.id ? "Edit Project" : "New Project"}
          </h3>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full hover:bg-gray-100 text-gray-500">
            <FaTimes />
          </button>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {/* Left: info */}
          <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4">
            <div className="text-sm font-semibold text-cyan-700">Project Information</div>

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Title *</label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-cyan-400 focus:border-cyan-400 outline-none"
                  value={values.title}
                  onChange={(e) => setValues((v) => ({ ...v, title: e.target.value }))}
                  placeholder="Project title"
                />
                <FieldError errors={errors} name="title" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Blurb / Description</label>
                <textarea
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-cyan-400 focus:border-cyan-400 outline-none"
                  value={values.blurb}
                  onChange={(e) => setValues((v) => ({ ...v, blurb: e.target.value }))}
                  placeholder="Short summary..."
                />
                <FieldError errors={errors} name="blurb" />
                <FieldError errors={errors} name="description" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700">Owner / Client</label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-cyan-400 focus:border-cyan-400 outline-none"
                    value={values.owner}
                    onChange={(e) => setValues((v) => ({ ...v, owner: e.target.value }))}
                    placeholder="Owner name"
                  />
                    <FieldError errors={errors} name="owner" />
                    <FieldError errors={errors} name="client" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700">Contract Value (LKR, millions)</label>
                  <input
                    type="number"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-cyan-400 focus:border-cyan-400 outline-none"
                    value={values.priceMillions}
                    onChange={(e) => setValues((v) => ({ ...v, priceMillions: e.target.value }))}
                    placeholder="e.g., 185 (means 185,000,000 LKR)"
                    step="0.01"
                  />
                  <p className="text-xs text-gray-500 mt-1">Saved as full amount (millions × 1,000,000).</p>
                  <FieldError errors={errors} name="price" />
                  <FieldError errors={errors} name="contract_value" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Address / Location</label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-cyan-400 focus:border-cyan-400 outline-none"
                  value={values.address}
                  onChange={(e) => setValues((v) => ({ ...v, address: e.target.value }))}
                  placeholder="Location"
                />
                <FieldError errors={errors} name="address" />
                <FieldError errors={errors} name="location" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Status</label>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-cyan-400 focus:border-cyan-400 outline-none"
                  value={values.status}
                  onChange={(e) => setValues((v) => ({ ...v, status: e.target.value }))}
                >
                  <option value={STATUS.ONGOING}>Ongoing</option>
                  <option value={STATUS.COMPLETED}>Completed</option>
                </select>
                <FieldError errors={errors} name="status" />
              </div>
            </div>
          </div>

          {/* Right: images */}
          <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4">
            <div className="text-sm font-semibold text-cyan-700">Images</div>

            {initial?.id && (
              <div className="mt-3">
                <div className="text-sm text-gray-600 mb-2">
                  Current gallery — click <FaStar className="inline -mt-0.5" /> to set cover, or trash to mark delete.
                </div>
                {existingList.length ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {existingList.map((src, i) => {
                      const isCover = coverExisting === src && coverNewIdx === -1;
                      const willDelete = markedDeletes.has(src);
                      return (
                        <div
                          key={`${i}-${src}`}
                          className={`relative rounded-lg overflow-hidden border ${
                            isCover ? "border-2 border-amber-400" : "border-gray-200"
                          } bg-white group`}
                        >
                          <button
                            type="button"
                            onClick={() => chooseCoverFromExisting(src)}
                            className="block w-full"
                            title={willDelete ? "Undo delete first to set as cover" : "Set as cover"}
                            disabled={willDelete}
                          >
                            <SafeImg
                              src={src}
                              alt={`existing-${i + 1}`}
                              className={`w-full h-20 object-cover ${willDelete ? "opacity-60" : ""}`}
                              fallback={THUMB_PLACEHOLDER}
                            />
                          </button>

                          <span className={`absolute top-1 left-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${isCover ? "bg-amber-400 text-white" : "bg-white/90 text-gray-700 border border-gray-200"}`}>
                            <FaStar className="text-[10px]" />
                            Cover
                          </span>

                          {willDelete && (
                            <span className="absolute top-1 right-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-red-500 text-white">
                              Will delete
                            </span>
                          )}

                          <button
                            type="button"
                            onClick={() => toggleMarkDeleteExisting(src)}
                            className={`absolute bottom-1 right-1 rounded-md border px-1.5 py-0.5 text-[10px] ${
                              willDelete
                                ? "border-gray-300 bg-white/95 text-gray-700 hover:bg-gray-50"
                                : "border-red-300 bg-white/95 text-red-600 hover:bg-red-50"
                            }`}
                            title={willDelete ? "Undo delete" : "Mark to delete on Save"}
                          >
                            <FaTrash /> {willDelete ? "Undo" : "Delete"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">No existing images.</div>
                )}
              </div>
            )}

            <div className="mt-4 space-y-4">
              <label className="flex items-center justify-center gap-2 cursor-pointer w-full rounded-xl border border-gray-300 bg-white px-4 py-3 hover:border-cyan-400 text-gray-700">
                <FaCloudUploadAlt />
                <span>Select images (you can select multiple)</span>
                <input type="file" accept="image/*" multiple onChange={(e) => onFiles(e.target.files)} hidden />
              </label>
              <FieldError errors={errors} name="img" />
              <FieldError errors={errors} name="images" />
              <FieldError errors={errors} name="images.0" />

              <div>
                <div className="text-sm text-gray-600 mb-2">Cover preview</div>
                <div className="bg-white rounded-xl border border-gray-300 p-2">
                  {coverPreviewSrc ? (
                    <SafeImg
                      src={coverPreviewSrc}
                      alt="cover-preview"
                      className="w-full h-32 md:h-40 object-cover rounded-lg"
                      fallback={PLACEHOLDER}
                    />
                  ) : (
                    <div className="h-32 md:h-40 grid place-items-center text-gray-400">No image selected</div>
                  )}
                </div>
              </div>

              {!!preview.length && (
                <div>
                  <div className="text-sm text-gray-600 mb-2">New uploads</div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {preview.map((src, i) => {
                      const isCover = coverNewIdx === i;
                      return (
                        <div
                          key={`new-${i}`}
                          className={`relative rounded-lg overflow-hidden border ${isCover ? "border-2 border-amber-400" : "border-gray-200"} bg-white`}
                        >
                          <button
                            type="button"
                            onClick={() => chooseCoverFromNew(i)}
                            className="block w-full"
                            title="Set as cover"
                          >
                            <SafeImg
                              src={src}
                              alt={`new-${i + 1}`}
                              className="w-full h-20 object-cover"
                              fallback={THUMB_PLACEHOLDER}
                            />
                          </button>
                          <span className={`absolute top-1 left-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${isCover ? "bg-amber-400 text-white" : "bg-white/90 text-gray-700 border border-gray-200"}`}>
                            <FaStar className="text-[10px]" />
                            Cover
                          </span>
                          <button
                            type="button"
                            onClick={() => askDeleteNew(i)}
                            className="absolute bottom-1 right-1 rounded-md border border-red-300 bg-white/95 text-red-600 hover:bg-red-50 px-1.5 py-0.5 text-[10px]"
                            title="Remove uploaded image"
                          >
                            <FaTrash /> Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={values.replaceGallery}
                  onChange={(e) => setValues((v) => ({ ...v, replaceGallery: e.target.checked }))}
                  className="rounded text-cyan-400 focus:ring-cyan-400"
                />
                Replace existing gallery (instead of appending)
              </label>
              {values.replaceGallery && (
                <p className="text-xs text-amber-600">
                  When replacing, server will drop all previous images and keep only the new uploads.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse sm:flex-row justify-end gap-3">
          <button className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50" onClick={onClose}>
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-lg bg-cyan-400 text-white hover:bg-cyan-500 disabled:opacity-60"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Project"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* =========================
  Preview Modal
  ========================= */
function ProjectPreview({ open, onClose, project }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [project?.id, open]);
  if (!project) return null;

  const rawImages = Array.isArray(project?.images) ? project.images : [];
  const images = [...(project?.img ? [project.img] : []), ...rawImages]
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .filter(Boolean);

  return (
    <Modal open={open} onClose={onClose} size="xl">
      <div className="p-4 sm:p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-extrabold text-gray-800">{project.title}</h3>
            <p className="text-gray-600 mt-1">{project.blurb}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full hover:bg-gray-100 text-gray-500">
            <FaTimes />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
            <div className="text-sm text-gray-500">Status</div>
            <div className="mt-1 font-semibold">
              {project.status === STATUS.COMPLETED ? (
                <Badge kind="success">Completed</Badge>
              ) : (
                <Badge kind="info">Ongoing</Badge>
              )}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
            <div className="text-sm text-gray-500">Owner</div>
            <div className="mt-1 font-semibold text-gray-800">{project.owner || "-"}</div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
            <div className="text-sm text-gray-500">Address</div>
            <div className="mt-1 font-semibold text-gray-800">{project.address || "-"}</div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
            <div className="text-sm text-gray-500">Contract Value</div>
            <div className="mt-1 font-semibold text-gray-800">
              {Number.isFinite(Number(project.price)) ? formatMillionsLKR(project.price) : "-"}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Gallery images={images} currentIndex={idx} setCurrentIndex={setIdx} />
        </div>
      </div>
    </Modal>
  );
}

/* =========================
  Main: Projects Manager
  ========================= */
export default function Projectsmanagement() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, ongoing: 0, completed: 0 });

  // Newest first by default
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [size] = useState(10);
  const [lastPage, setLastPage] = useState(1);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [preview, setPreview] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const [delConfirm, setDelConfirm] = useState({ open: false, row: null });

  const { push: toast, View: Toasts } = useToasts();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      if (status && status !== "all") params.set("status", status);
      if (sortBy) params.set("sort", sortBy);
      if (dir) params.set("dir", dir);
      params.set("page", String(page));
      params.set("size", String(size));

      const url = apiPath(`/projects?${params.toString()}`);
      const res = await api.get(url);

      const payload = res.data || {};
      const data = Array.isArray(payload.data) ? payload.data : Array.isArray(payload) ? payload : [];

      setRows(
        data.map((p) => ({
          id: p.id,
          title: p.title,
          blurb: p.blurb,
          owner: p.owner,
          address: p.address,
          price: p.price,
          img: p.img,
          images: Array.isArray(p.images) ? p.images.filter(Boolean) : [],
          status:
            p.status === STATUS.COMPLETED || p.status === STATUS.ONGOING
              ? p.status
              : p.status === "finished"
              ? STATUS.COMPLETED
              : STATUS.ONGOING,
        }))
      );

      const c = payload.counts || {};
      const total = payload.meta?.total ?? payload.total ?? data.length;
      const computed = {
        total,
        ongoing: (data || []).filter((x) => x.status === STATUS.ONGOING || x.status !== STATUS.COMPLETED).length,
        completed: (data || []).filter((x) => x.status === STATUS.COMPLETED || x.status === "finished").length,
      };
      setStats({
        total: c.total ?? computed.total,
        ongoing: c.ongoing ?? computed.ongoing,
        completed: c.completed ?? c.finished ?? computed.completed,
      });

      const lp =
        payload.meta?.last_page ??
        payload.last_page ??
        (total ? Math.max(1, Math.ceil(total / size)) : 1);
      setLastPage(lp);
      if (page > lp) setPage(lp);
    } catch (e) {
      console.error("Fetch projects failed:", e?.response?.data || e.message);
      toast("error", "Unable to fetch", e?.response?.data?.message || e.message || "Network error");
    } finally {
      setLoading(false);
    }
  }, [q, status, sortBy, dir, page, size, toast]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const filtered = rows;

  const openPreviewFor = (row) => {
    setPreview(row);
    setOpenPreview(true);
  };

  const toggleStatus = async (row) => {
    const next = row.status === STATUS.COMPLETED ? STATUS.ONGOING : STATUS.COMPLETED;
    const payload = { status: next };
    try {
      await api.patch(apiPath(`/projects/${row.id}/status`), payload);
      toast("success", next === STATUS.COMPLETED ? "Marked completed" : "Reopened", row.title);
    } catch (e1) {
      const code = e1?.response?.status;
      if (code === 405 || code === 404) {
        try {
          await api.patch(apiPath(`/projects/${row.id}`), payload);
          toast("success", next === STATUS.COMPLETED ? "Marked completed" : "Reopened", row.title);
        } catch (e2) {
          if (e2?.response?.status === 405 || e2?.response?.status === 404) {
            const fd = new FormData();
            fd.append("status", next);
            fd.append("_method", "PATCH");
            await api.post(apiPath(`/projects/${row.id}`), fd);
            toast("success", next === STATUS.COMPLETED ? "Marked completed" : "Reopened", row.title);
          } else {
            const m = e2?.response?.data?.message || e2.message;
            toast("error", "Failed to update status", m);
            return;
          }
        }
      } else {
        const m = e1?.response?.data?.message || e1.message;
        toast("error", "Failed to update status", m);
        return;
      }
    }
    await fetchList();
  };

  const handleDeleteProject = async (row) => {
    setDelConfirm({ open: false, row: null });
    if (!row) return;
    try {
      await api.delete(apiPath(`/projects/${row.id}`));
      toast("success", "Project deleted", row.title);
      fetchList();
    } catch (e) {
      const m = e?.response?.data?.message || e.message;
      toast("error", "Delete failed", m);
    }
  };

  const pages = useMemo(() => {
    const out = [];
    const max = Math.max(1, lastPage);
    const start = Math.max(1, Math.min(page - 2, max - 4));
    const end = Math.min(max, start + 4);
    for (let i = start; i <= end; i++) out.push(i);
    return out;
  }, [page, lastPage]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toasts />

      {/* Delete confirmation for project */}
      <Confirm
        open={delConfirm.open}
        title={`Delete project: ${delConfirm.row?.title || ""}?`}
        message="This action cannot be undone."
        onCancel={() => setDelConfirm({ open: false, row: null })}
        onConfirm={() => handleDeleteProject(delConfirm.row)}
      />

      {/* top bar */}
      <div className="bg-cyan-400 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-extrabold text-lg">Projects Manager</div>
              <div className="text-sm text-white/90">Manage all construction projects</div>
            </div>

            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/Admin/ContactManagement"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-cyan-700 hover:bg-white/90"
                title="Go to Contact Management"
              >
                <FaUsers /> Contact Management
              </Link>
            </div>

            <button
              className="md:hidden w-8 h-8 grid place-items-center rounded-lg bg-white/20"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              title="Menu"
            >
              <FaBars />
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="mt-3 md:hidden">
              <Link
                to="/admin/contacts"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-cyan-700 hover:bg-white/90"
                title="Go to Contact Management"
              >
                <FaUsers /> Contact Management
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard label="Total Projects" value={stats.total} ok icon={<FaImages />} />
          <StatCard label="Ongoing" value={stats.ongoing} ok={false} icon={<FaSync />} />
          <StatCard label="Completed" value={stats.completed} ok icon={<FaCheck />} />
        </div>

        {/* actions */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={fetchList}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <FaSync /> Refresh
            </button>
            <button
              onClick={() => {
                setEditRow(null);
                setOpenForm(true);
              }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-400 text-white hover:bg-cyan-500"
            >
              <FaPlus /> New Project
            </button>
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:flex-none">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={q}
                onChange={(e) => {
                  setPage(1);
                  setQ(e.target.value);
                }}
                placeholder="Search projects..."
                className="pl-9 pr-3 py-2 rounded-lg bg-white border border-gray-300 w-full md:w-72 focus:ring-cyan-400 focus:border-cyan-400 outline-none"
              />
            </div>
            <button
              className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 hidden md:block"
              onClick={() => {
                setQ("");
                setStatus("all");
                setSortBy("created_at");
                setDir("desc");
                setPage(1);
              }}
            >
              Reset
            </button>
          </div>
        </div>

        {/* filter row */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                <FaFilter className="text-cyan-400" />
                Filter by Status
              </span>
              <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                {["all", STATUS.ONGOING, STATUS.COMPLETED].map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setStatus(s);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                      status === s ? "bg-cyan-400 text-white shadow-sm" : "text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="md:ml-auto flex items-center gap-2 text-sm bg-gray-100 p-1 rounded-lg">
              <FaSortAmountDown className="text-gray-500 ml-1" />
              <select
                className="rounded-md bg-transparent px-2 py-1.5 border-none focus:ring-0 text-gray-700"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
              >
                <option value="created_at">Date Added</option>
                <option value="id">ID</option>
                <option value="title">Title</option>
                <option value="owner">Owner</option>
                <option value="price">Contract Value</option>
              </select>
              <select
                className="rounded-md bg-transparent px-2 py-1.5 border-none focus:ring-0 text-gray-700"
                value={dir}
                onChange={(e) => {
                  setDir(e.target.value);
                  setPage(1);
                }}
              >
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
              </select>
            </div>
          </div>
        </div>

        {/* Mobile reset */}
        <div className="md:hidden">
          <button
            className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => {
              setQ("");
              setStatus("all");
              setSortBy("created_at");
              setDir("desc");
              setPage(1);
            }}
          >
            Reset all filters
          </button>
        </div>

        {/* table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left p-3 w-12">Cover</th>
                  <th className="text-left p-3">Title & Description</th>
                  <th className="text-left p-3 hidden lg:table-cell">Owner</th>
                  <th className="text-left p-3 hidden md:table-cell">Address</th>
                  <th className="text-left p-3 hidden xl:table-cell">Contract Value</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3 hidden sm:table-cell">Images</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                )}

                {!loading &&
                  filtered.map((p) => {
                    const coverCandidate = p.img || (Array.isArray(p.images) && p.images[0]);
                    const count =
                      (Array.isArray(p.images) ? p.images.filter(Boolean).length : 0) || (p.img ? 1 : 0);

                    return (
                      <tr key={p.id} className="border-t border-gray-200 hover:bg-gray-50">
                        <td className="p-3">
                          <SafeImg
                            src={coverCandidate}
                            alt={`${p.title} cover`}
                            className="w-10 h-10 rounded object-cover border border-gray-200"
                            fallback={PLACEHOLDER}
                          />
                        </td>
                        <td className="p-3">
                          <div className="font-semibold text-gray-800">{p.title}</div>
                          <div className="text-gray-500 line-clamp-1 text-xs">{p.blurb}</div>
                        </td>
                        <td className="p-3 text-gray-700 hidden lg:table-cell">{p.owner || "-"}</td>
                        <td className="p-3 text-gray-700 hidden md:table-cell">{p.address || "-"}</td>
                        <td className="p-3 text-gray-700 hidden xl:table-cell">
                          {Number.isFinite(Number(p.price)) ? formatMillionsLKR(p.price) : "-"}
                        </td>
                        <td className="p-3">
                          {p.status === STATUS.COMPLETED ? (
                            <Badge kind="success">Completed</Badge>
                          ) : (
                            <Badge kind="info">Ongoing</Badge>
                          )}
                        </td>
                        <td className="p-3 text-gray-700 hidden sm:table-cell">
                          {(Array.isArray(p.images) ? p.images.filter(Boolean).length : 0) || (p.img ? 1 : 0)}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <button
                              className="w-7 h-7 grid place-items-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                              title="Preview"
                              onClick={() => openPreviewFor(p)}
                            >
                              <FaEye className="text-xs" />
                            </button>
                            <button
                              className="w-7 h-7 grid place-items-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                              title="Edit"
                              onClick={() => {
                                setEditRow(p);
                                setOpenForm(true);
                              }}
                            >
                              <FaEdit className="text-xs" />
                            </button>
                            <button
                              className={`w-7 h-7 grid place-items-center rounded-lg ${
                                p.status === STATUS.COMPLETED
                                  ? "border border-green-300 text-green-600 hover:bg-green-50"
                                  : "border border-cyan-300 text-cyan-600 hover:bg-cyan-50"
                              }`}
                              title={p.status === STATUS.COMPLETED ? "Reopen" : "Mark Completed"}
                              onClick={() => toggleStatus(p)}
                            >
                              {p.status === STATUS.COMPLETED ? <FaTimes className="text-xs" /> : <FaCheck className="text-xs" />}
                            </button>
                            <button
                              className="w-7 h-7 grid place-items-center rounded-lg border border-red-300 text-red-600 hover:bg-red-50"
                              title="Delete"
                              onClick={() => setDelConfirm({ open: true, row: p })}
                            >
                              <FaTrash className="text-xs" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                {!loading && !filtered.length && (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-gray-500">
                      No projects found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* pagination */}
          <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-500">Page {page} of {lastPage}</div>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              <button
                className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 disabled:opacity-50 hover:bg-gray-50 text-sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Prev
              </button>
              {pages.map((pnum) => (
                <button
                  key={pnum}
                  onClick={() => setPage(pnum)}
                  className={`px-3 py-1.5 rounded-lg border border-gray-300 text-sm ${
                    pnum === page ? "bg-cyan-400 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {pnum}
                </button>
              ))}
              <button
                className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 disabled:opacity-50 hover:bg-gray-50 text-sm"
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page >= lastPage}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ProjectPreview open={openPreview} onClose={() => setOpenPreview(false)} project={preview} />
      <ProjectForm open={openForm} onClose={() => setOpenForm(false)} onSaved={fetchList} initial={editRow} />
    </div>
  );
}
